import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import { nanoid } from 'nanoid';
import {
  DndContext,
  PointerSensor,
  KeyboardSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { INSTRUMENTS } from '../../../constants/Instruments';
import { EXPERIENCE_LEVELS } from '../../../constants/experienceLevels';
import SkillLevelChip from '../../atoms/SkillLevelChip';
import { PencilIcon } from '../../atoms/icons/EditorialProfileIcons';
import './index.scss';

const DragHandleIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
    <line x1="4" y1="8" x2="20" y2="8" />
    <line x1="4" y1="16" x2="20" y2="16" />
  </svg>
);

const MENU_GAP = 8;
const VIEWPORT_PADDING = 12;

function usePortalRoot() {
  return typeof document !== 'undefined'
    ? (document.getElementById('portal') || document.body)
    : null;
}

/* -------------------------------------------------------------------------- */
/*  Sub-component: instrument autocomplete                                    */
/* -------------------------------------------------------------------------- */
function InstrumentAutocomplete({ value, onChange, placeholder, ariaLabel }) {
  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const menuRef = useRef(null);
  const [draft, setDraft] = useState(value || '');
  const [isOpen, setIsOpen] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0, width: 0 });
  const lastCommittedRef = useRef(value || '');
  // Keep the current draft accessible from event closures (blur fires before
  // React re-renders, so the onBlur handler would otherwise read a stale draft).
  const draftRef = useRef(value || '');
  // Set true right before we programmatically blur after picking an option, so
  // the blur handler doesn't immediately commit the stale empty draft.
  const suppressBlurRef = useRef(false);

  useEffect(() => {
    setDraft(value || '');
    draftRef.current = value || '';
    lastCommittedRef.current = value || '';
  }, [value]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const handleDocClick = (e) => {
      const inTrigger = containerRef.current?.contains(e.target);
      const inMenu = menuRef.current?.contains(e.target);
      if (!inTrigger && !inMenu) setIsOpen(false);
    };
    const handleKey = (e) => {
      if (e.key === 'Escape') {
        const previous = lastCommittedRef.current || '';
        suppressBlurRef.current = true;
        draftRef.current = previous;
        setDraft(previous);
        setIsOpen(false);
        inputRef.current?.blur();
      }
    };
    document.addEventListener('mousedown', handleDocClick);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleDocClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, [isOpen]);

  useLayoutEffect(() => {
    if (!isOpen) return undefined;
    const compute = () => {
      const trigger = inputRef.current;
      if (!trigger) return;
      const r = trigger.getBoundingClientRect();
      const vh = window.innerHeight;
      const vw = window.innerWidth;
      const menu = menuRef.current;
      const h = menu ? menu.getBoundingClientRect().height : 240;
      let top = r.bottom + MENU_GAP;
      let left = r.left;
      if (top + h + VIEWPORT_PADDING > vh) {
        const flipped = r.top - MENU_GAP - h;
        if (flipped >= VIEWPORT_PADDING) top = flipped;
        else top = Math.max(VIEWPORT_PADDING, vh - h - VIEWPORT_PADDING);
      }
      const desiredWidth = Math.max(r.width, 240);
      const maxLeft = vw - desiredWidth - VIEWPORT_PADDING;
      if (left > maxLeft) left = maxLeft;
      if (left < VIEWPORT_PADDING) left = VIEWPORT_PADDING;
      setCoords({ top, left, width: desiredWidth });
    };
    compute();
    const onScroll = () => compute();
    const onResize = () => compute();
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onResize);
    };
  }, [isOpen, draft]);

  const normalizedDraft = (draft || '').trim().toLowerCase();
  const filtered = useMemo(() => {
    if (!normalizedDraft) return INSTRUMENTS;
    return INSTRUMENTS.filter((name) => name.toLowerCase().includes(normalizedDraft));
  }, [normalizedDraft]);

  const exact = useMemo(() => {
    if (!normalizedDraft) return null;
    return INSTRUMENTS.find((n) => n.toLowerCase() === normalizedDraft) || null;
  }, [normalizedDraft]);

  const commit = useCallback((next) => {
    const value = (next || '').trim();
    const result = onChange?.(value);
    if (result && typeof result.then === 'function') {
      return result.then((saved) => {
        if (saved !== false) {
          lastCommittedRef.current = value;
        }
        return saved;
      }).catch((err) => {
        console.error('InstrumentAutocomplete save failed', err);
        return false;
      });
    }
    lastCommittedRef.current = value;
    return Promise.resolve(true);
  }, [onChange]);

  const selectOption = useCallback((label) => {
    suppressBlurRef.current = true;
    draftRef.current = label;
    setDraft(label);
    commit(label);
    setIsOpen(false);
    inputRef.current?.blur();
  }, [commit]);

  const portalRoot = usePortalRoot();

  return (
    <div ref={containerRef} className="instrument-experience-editor__instrument">
      <input
        ref={inputRef}
        type="text"
        className="instrument-experience-editor__input"
        value={draft}
        placeholder={placeholder}
        aria-label={ariaLabel}
        autoComplete="off"
        onFocus={() => setIsOpen(true)}
        onChange={(e) => {
          const nextDraft = e.target.value;
          draftRef.current = nextDraft;
          setDraft(nextDraft);
          if (!isOpen) setIsOpen(true);
        }}
        onBlur={() => {
          // Skip the blur commit if we just selected an option — the option
          // handler already committed the correct value, and the blur fires
          // synchronously before the input re-renders with the new draft.
          if (suppressBlurRef.current) {
            suppressBlurRef.current = false;
            return;
          }
          setTimeout(() => {
            if (containerRef.current?.contains(document.activeElement) || menuRef.current?.contains(document.activeElement)) return;
            commit(draftRef.current);
            setIsOpen(false);
          }, 80);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            suppressBlurRef.current = true;
            commit(draftRef.current);
            setIsOpen(false);
            inputRef.current?.blur();
          }
        }}
      />
      {isOpen && portalRoot ? ReactDOM.createPortal(
        <div
          ref={menuRef}
          className="instrument-experience-editor__menu"
          role="listbox"
          style={{ top: coords.top, left: coords.left, width: coords.width || undefined }}
        >
          {filtered.length === 0 && !draft.trim() ? (
            <div className="instrument-experience-editor__empty">No instruments</div>
          ) : null}
          {filtered.map((name) => {
            const isSelected = name === value;
            return (
              <button
                key={name}
                type="button"
                role="option"
                aria-selected={isSelected}
                className={`instrument-experience-editor__option${isSelected ? ' instrument-experience-editor__option--selected' : ''}`}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => selectOption(name)}
              >
                <span className="instrument-experience-editor__option-label">{name}</span>
              </button>
            );
          })}
          {draft.trim() && !exact ? (
            <button
              type="button"
              role="option"
              aria-selected={false}
              className="instrument-experience-editor__option instrument-experience-editor__option--custom"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => selectOption(draft.trim())}
            >
              <span className="instrument-experience-editor__option-label">{draft.trim()}</span>
              <span className="instrument-experience-editor__option-sub">Use custom instrument</span>
            </button>
          ) : null}
        </div>,
        portalRoot,
      ) : null}
    </div>
  );
}

InstrumentAutocomplete.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func,
  placeholder: PropTypes.string,
  ariaLabel: PropTypes.string,
};

/* -------------------------------------------------------------------------- */
/*  Sub-component: skill-level select                                         */
/* -------------------------------------------------------------------------- */
function SkillLevelSelect({ value, onChange, ariaLabel }) {
  const containerRef = useRef(null);
  const buttonRef = useRef(null);
  const menuRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0 });

  useEffect(() => {
    if (!isOpen) return undefined;
    const handleDocClick = (e) => {
      const inTrigger = containerRef.current?.contains(e.target);
      const inMenu = menuRef.current?.contains(e.target);
      if (!inTrigger && !inMenu) setIsOpen(false);
    };
    const handleKey = (e) => { if (e.key === 'Escape') setIsOpen(false); };
    document.addEventListener('mousedown', handleDocClick);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleDocClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, [isOpen]);

  useLayoutEffect(() => {
    if (!isOpen) return undefined;
    const compute = () => {
      const trigger = buttonRef.current;
      if (!trigger) return;
      const r = trigger.getBoundingClientRect();
      const vh = window.innerHeight;
      const vw = window.innerWidth;
      const menuH = menuRef.current ? menuRef.current.getBoundingClientRect().height : 260;
      const menuW = menuRef.current ? menuRef.current.getBoundingClientRect().width : 200;
      let top = r.bottom + MENU_GAP;
      let left = r.right - Math.max(menuW, 200);
      if (top + menuH + VIEWPORT_PADDING > vh) {
        const flipped = r.top - MENU_GAP - menuH;
        if (flipped >= VIEWPORT_PADDING) top = flipped;
        else top = Math.max(VIEWPORT_PADDING, vh - menuH - VIEWPORT_PADDING);
      }
      const maxLeft = vw - Math.max(menuW, 200) - VIEWPORT_PADDING;
      if (left > maxLeft) left = maxLeft;
      if (left < VIEWPORT_PADDING) left = VIEWPORT_PADDING;
      setCoords({ top, left });
    };
    compute();
    const onScroll = () => compute();
    const onResize = () => compute();
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onResize);
    };
  }, [isOpen]);

  const portalRoot = usePortalRoot();
  const currentLevel = value || EXPERIENCE_LEVELS[0]?.value;

  return (
    <div ref={containerRef} className="instrument-experience-editor__level">
      <button
        ref={buttonRef}
        type="button"
        className="instrument-experience-editor__level-trigger"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-label={ariaLabel || 'Experience level'}
        onClick={() => setIsOpen((v) => !v)}
      >
        <SkillLevelChip level={currentLevel}>{currentLevel}</SkillLevelChip>
      </button>
      {isOpen && portalRoot ? ReactDOM.createPortal(
        <div
          ref={menuRef}
          className="instrument-experience-editor__menu instrument-experience-editor__menu--levels"
          role="listbox"
          style={{ top: coords.top, left: coords.left }}
        >
          {EXPERIENCE_LEVELS.map((level) => {
            const isSelected = level.value === currentLevel;
            return (
              <button
                key={level.value}
                type="button"
                role="option"
                aria-selected={isSelected}
                className={`instrument-experience-editor__option${isSelected ? ' instrument-experience-editor__option--selected' : ''}`}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => { onChange?.(level.value); setIsOpen(false); }}
              >
                <SkillLevelChip level={level.value}>{level.value}</SkillLevelChip>
              </button>
            );
          })}
        </div>,
        portalRoot,
      ) : null}
    </div>
  );
}

SkillLevelSelect.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func,
  ariaLabel: PropTypes.string,
};

/* -------------------------------------------------------------------------- */
/*  Main editor                                                               */
/* -------------------------------------------------------------------------- */
const normalizeRowsFromProps = (experiences, levelField) => (
  Array.isArray(experiences) && experiences.length > 0
    ? experiences.map((row) => ({
        id: row.id || nanoid(),
        instrument: typeof row.instrument === 'string' ? row.instrument : '',
        [levelField]: typeof row[levelField] === 'string' && row[levelField] ? row[levelField] : 'Beginner',
        ...(typeof row.note === 'string' ? { note: row.note } : {}),
      }))
    : []
);

const rowSignature = (rows, levelField) => (
  (rows || [])
    .filter((r) => (r.instrument || '').trim())
    .map((r) => `${(r.instrument || '').trim()}|${(r[levelField] || '').trim()}`)
    .join('::')
);

export default function InstrumentExperienceEditor({
  experiences = [],
  onChange,
  addLabel = 'Add instrument',
  removeLabel = 'Remove',
  instrumentPlaceholder = 'Pick an instrument',
  levelField = 'years',
}) {
  /*
   * The editor owns its own working list so the user can keep an empty row
   * visible while they're picking an instrument. We sync FROM props only
   * when our local non-empty rows already match what the server has (i.e.
   * there's no in-flight edit); otherwise the inbound prop change is
   * ignored to preserve the empty row.
   */
  const [localRows, setLocalRows] = useState(() => normalizeRowsFromProps(experiences, levelField));
  const committedSignatureRef = useRef(rowSignature(localRows, levelField));
  const pendingSignaturesRef = useRef(new Set());

  useEffect(() => {
    const incoming = normalizeRowsFromProps(experiences, levelField);
    const incomingSig = rowSignature(incoming, levelField);
    committedSignatureRef.current = incomingSig;
    const hasEmptyLocal = localRows.some((r) => !(r.instrument || '').trim());
    if (hasEmptyLocal) {
      const localFilledSig = rowSignature(localRows, levelField);
      if (incomingSig === localFilledSig) return;
      const emptyTail = localRows.filter((r) => !(r.instrument || '').trim());
      setLocalRows([...incoming, ...emptyTail]);
      return;
    }
    if (rowSignature(localRows, levelField) === rowSignature(incoming, levelField)) return;
    setLocalRows(incoming);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [experiences]);

  const commitToParent = useCallback((nextRows) => {
    const filledSig = rowSignature(nextRows, levelField);
    if (filledSig === committedSignatureRef.current) {
      return Promise.resolve(true);
    }
    if (pendingSignaturesRef.current.has(filledSig)) {
      return Promise.resolve(false);
    }
    if (typeof onChange !== 'function') {
      committedSignatureRef.current = filledSig;
      return Promise.resolve(true);
    }

    pendingSignaturesRef.current.add(filledSig);
    let result;
    try {
      result = onChange(nextRows);
    } catch (err) {
      pendingSignaturesRef.current.delete(filledSig);
      console.error('InstrumentExperienceEditor save failed', err);
      return Promise.resolve(false);
    }

    return Promise.resolve(result)
      .then(() => {
        committedSignatureRef.current = filledSig;
        return true;
      })
      .catch((err) => {
        console.error('InstrumentExperienceEditor save failed', err);
        return false;
      })
      .finally(() => {
        pendingSignaturesRef.current.delete(filledSig);
      });
  }, [onChange, levelField]);

  const handleRowChange = useCallback((index, field, nextValue) => {
    setLocalRows((rows) => {
      const next = rows.map((row, i) => (
        i === index ? { ...row, [field]: nextValue } : row
      ));
      commitToParent(next);
      return next;
    });
  }, [commitToParent]);

  const handleRemove = useCallback((index) => {
    setLocalRows((rows) => {
      const next = rows.filter((_, i) => i !== index);
      // Always tell the parent — even removing an empty row should sync if
      // the parent cares; the serializer will drop empties anyway.
      if (typeof onChange === 'function') onChange(next);
      return next;
    });
  }, [onChange]);

  const handleAdd = useCallback(() => {
    // Local-only — adding an empty row doesn't change the saved payload
    // (the serializer would drop it). We surface the row in the UI so the
    // user can pick an instrument, and `onChange` fires when the instrument
    // gets a value.
    setLocalRows((rows) => [...rows, { id: nanoid(), instrument: '', [levelField]: 'Beginner' }]);
  }, [levelField]);

  // Drag-and-drop sensors — pointer with 6px activation distance so simple
  // clicks on the row don't accidentally start a drag, plus keyboard support.
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragEnd = useCallback((event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setLocalRows((rows) => {
      const oldIndex = rows.findIndex((r) => r.id === active.id);
      const newIndex = rows.findIndex((r) => r.id === over.id);
      if (oldIndex === -1 || newIndex === -1) return rows;
      const next = arrayMove(rows, oldIndex, newIndex);
      if (typeof onChange === 'function') onChange(next);
      return next;
    });
  }, [onChange]);

  const safeRows = localRows;
  const rowIds = useMemo(() => safeRows.map((r) => r.id), [safeRows]);

  return (
    <div className="instrument-experience-editor">
      {safeRows.length > 0 ? (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={rowIds} strategy={verticalListSortingStrategy}>
            <ul className="instrument-experience-editor__list">
              {safeRows.map((row, index) => (
                <SortableRow
                  key={row.id}
                  rowId={row.id}
                  isMain={index === 0 && (row.instrument || '').trim().length > 0}
                  instrumentValue={row.instrument}
                  levelValue={row[levelField]}
                  instrumentPlaceholder={instrumentPlaceholder}
                  onInstrumentChange={(next) => handleRowChange(index, 'instrument', next)}
                  onLevelChange={(next) => handleRowChange(index, levelField, next)}
                  onRemove={() => handleRemove(index)}
                  removeLabel={removeLabel}
                />
              ))}
            </ul>
          </SortableContext>
        </DndContext>
      ) : null}
      <button
        type="button"
        className="instrument-experience-editor__add"
        onClick={handleAdd}
      >
        <span className="instrument-experience-editor__add-plus" aria-hidden="true">+</span>
        {addLabel}
        <PencilIcon width="14" height="14" />
      </button>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Sortable row — wraps the row content with a drag handle.                 */
/* -------------------------------------------------------------------------- */
function SortableRow({
  rowId,
  isMain,
  instrumentValue,
  levelValue,
  instrumentPlaceholder,
  onInstrumentChange,
  onLevelChange,
  onRemove,
  removeLabel,
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: rowId });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };
  const classes = [
    'instrument-experience-editor__row',
    isMain ? 'instrument-experience-editor__row--main' : '',
    isDragging ? 'instrument-experience-editor__row--dragging' : '',
  ].filter(Boolean).join(' ');

  return (
    <li ref={setNodeRef} className={classes} style={style}>
      <button
        type="button"
        className="instrument-experience-editor__drag"
        aria-label="Reorder instrument"
        {...attributes}
        {...listeners}
      >
        <DragHandleIcon width="16" height="16" aria-hidden="true" />
      </button>
      {isMain ? (
        <span className="instrument-experience-editor__main-badge" aria-label="Main instrument">Main</span>
      ) : null}
      <InstrumentAutocomplete
        value={instrumentValue}
        placeholder={instrumentPlaceholder}
        ariaLabel="Instrument"
        onChange={onInstrumentChange}
      />
      <SkillLevelSelect
        value={levelValue}
        onChange={onLevelChange}
      />
      <button
        type="button"
        className="instrument-experience-editor__remove"
        onClick={onRemove}
        aria-label={removeLabel}
      >
        <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M18 6L6 18M6 6l12 12" />
        </svg>
      </button>
    </li>
  );
}

SortableRow.propTypes = {
  rowId: PropTypes.string.isRequired,
  isMain: PropTypes.bool,
  instrumentValue: PropTypes.string,
  levelValue: PropTypes.string,
  instrumentPlaceholder: PropTypes.string,
  onInstrumentChange: PropTypes.func,
  onLevelChange: PropTypes.func,
  onRemove: PropTypes.func,
  removeLabel: PropTypes.string,
};

InstrumentExperienceEditor.propTypes = {
  experiences: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    instrument: PropTypes.string,
  })),
  onChange: PropTypes.func,
  addLabel: PropTypes.node,
  removeLabel: PropTypes.string,
  instrumentPlaceholder: PropTypes.string,
  levelField: PropTypes.string,
};
